import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { RanaAlert, RanaAlertProps, RanaTable } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";
import { type ThreediModel, type ThreediModelTask } from "@nens/threedi/openapi-client";

import { useHccModelTasksQuery } from "@/queries/query-hcc-model-task";
import { modelIsBeingGenerated } from "@/utils/model-is-being-generated";

import { MODEL_TASK_TABLE_ID } from "../consts/model-task-table-id";
import { useSchematisationDetailsContext } from "../schematisation-details-context";

function getTaskDetail(task: ThreediModelTask): { progress?: number; message?: string } {
  return (task.detail as { progress?: number; message?: string } | null) ?? {};
}

function getProgressChakraStatus(status: ThreediModelTask["status"]): RanaAlertProps["status"] {
  if (status === "failure" || status === "revoked") return "error";
  if (status === "success") return "success";
  return "inProgress";
}

function TaskProgressBar({ task }: { task: ThreediModelTask }) {
  const progressPct = (getTaskDetail(task).progress ?? 0) * 100;

  return (
    <RanaAlert
      progress={progressPct}
      variant="icon-only"
      iconSize={6}
      status={getProgressChakraStatus(task.status)}
    />
  );
}

export function SchematisationModelTasksSection() {
  const { threediModel } = useSchematisationDetailsContext();

  // Prevent rendering the model tasks section if the model is not being generated, to avoid fetching the tasks unnecessarily.
  if (!threediModel || !modelIsBeingGenerated(threediModel)) return null;

  return (
    <AnimateSize id={MODEL_TASK_TABLE_ID}>
      <SchematisationModelTasksSectionInner threediModel={threediModel} />
    </AnimateSize>
  );
}

export function SchematisationModelTasksSectionInner({
  threediModel,
}: {
  threediModel: ThreediModel;
}) {
  const intl = useIntl();
  const { data: tasksResponse, isLoading } = useHccModelTasksQuery(threediModel.id);
  const tasks = [...(tasksResponse?.results ?? [])].reverse(); // Reverse the tasks so that the most recent task is shown last.

  return (
    <RanaTable
      columnDefinitions={[
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "file.model.generate.task_name",
            defaultMessage: "Task name",
          }),
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "file.model.generate.status",
            defaultMessage: "Status",
          }),
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "file.model.generate.progress",
            defaultMessage: "Progress",
          }),
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "file.model.generate.message",
            defaultMessage: "Message",
          }),
        },
      ]}
      tableDataIds={tasks.map((task) => String(task.id))}
      isLoading={isLoading}
      emptyElement={
        <RanaTable.Empty>
          {intl.formatMessage({
            id: "file.schematisation.no_model_tasks",
            defaultMessage: "No model tasks found.",
          })}
        </RanaTable.Empty>
      }
    >
      <RanaTable.Container minHeight="auto" height="auto">
        <RanaTable.Header />
        <RanaTable.Body>
          {tasks.map((task) => (
            <RanaTable.Row key={String(task.id)} rowId={String(task.id)}>
              <RanaTable.Cell>{capitalizeFirst(task.name.replace(/_/g, " "))}</RanaTable.Cell>
              <RanaTable.Cell>{capitalizeFirst(task.status)}</RanaTable.Cell>
              <RanaTable.Cell>
                <Box w="full">
                  <TaskProgressBar task={task} />
                </Box>
              </RanaTable.Cell>
              <RanaTable.Cell>{getTaskDetail(task).message ?? ""}</RanaTable.Cell>
            </RanaTable.Row>
          ))}
        </RanaTable.Body>
      </RanaTable.Container>
    </RanaTable>
  );
}
